import { Directive, Input, numberAttribute } from '@angular/core';

/** Shared keyboard focus ring — see `.focus-outline` in `src/scss/core/utilities.scss`. */
@Directive({
  selector: '[focus-outline]',
  standalone: true,
  host: {
    class: 'focus-outline',
    '[style.--focus-outline-offset.px]': 'focusOutline',
  },
})
export class FocusOutlineDirective {
  /**
   * Outline offset in pixels (same meaning as `focus-outline($offset)` in mixins).
   * Default 2, so the ring is drawn outside the control. Example:
   * `[focus-outline]="-2"` for tighter icon-button rings.
   *
   * **Inert on `<input>` and `<textarea>` elements that are not
   * `.mdc-text-field__input`.** The product-wide
   * `textarea:not(.mdc-text-field__input), input:not(.mdc-text-field__input)`
   * rule in `src/scss/core/global.scss` compiles to `(0,2,1)` — `:not()` takes
   * the specificity of its argument — while the `.focus-outline` class this
   * directive applies is `(0,2,0)`, so the global rule's hard-coded
   * `outline-offset: 2px` wins regardless of source order. The binding still
   * sets `--focus-outline-offset` on the element, but the only declaration that
   * reads it has already lost, and an inline custom property does not raise the
   * specificity of the rule consuming it. The ring itself is unaffected: both
   * rules resolve to the same 2px Blue 700 outline, so a text control carrying
   * the bare attribute shows exactly one correct indicator.
   *
   * A text control that needs a different offset takes a component-scoped
   * `@include focus-outline(n)` at `(0,2,1)` or higher instead of a binding
   * here. Left deliberately at this specificity (ISSUE-19, Option B): wrapping
   * the global selector in `:where()` would drop it to `(0,1,0)` and let any
   * component-local suppression written on a text control's `:focus` re-win —
   * the regression ISSUE-07 recorded.
   */
  @Input({
    alias: 'focus-outline',
    transform: (value: unknown) => numberAttribute(value, 2),
  })
  focusOutline = 2;
}
